import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { io, Socket } from 'socket.io-client';

const isDev = import.meta.env.DEV;
const SOCKET_IO_URL = import.meta.env.VITE_SOCKET_IO_URL || (isDev ? 'http://localhost:5000' : 'https://campus-connect.fly.dev');
const WS_URL = import.meta.env.VITE_WS_URL || (isDev ? 'ws://localhost:5000' : 'wss://campus-connect.fly.dev');
let currentSocketIoUrl = SOCKET_IO_URL;
let currentWSUrl = WS_URL;

export function useRealtime(userId: string | undefined) {
  const socketRef = useRef<Socket | null>(null);
  const ws = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout>>();
  const shouldReconnect = useRef(true);
  const isConnecting = useRef(false);

  const syncRideCaches = useCallback((updater?: (rides: any[]) => any[]) => {
    const queryGroups = [['myRides'], ['availableRides']];

    queryGroups.forEach((queryKey) => {
      queryClient.setQueriesData({ queryKey }, (old: any) => {
        if (!updater || !Array.isArray(old)) return old;
        return updater(old);
      });
      queryClient.invalidateQueries({ queryKey });
    });
  }, [queryClient]);

  const invalidateRequestAndNotificationCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['myRequests'] });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    queryClient.invalidateQueries({ queryKey: ['myRides'] });
    queryClient.invalidateQueries({ queryKey: ['availableRides'] });
  }, [queryClient]);

  const handleRealtimeMessage = useCallback((rawData: any) => {
    try {
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      if (!data?.type) return;

      console.log('[Realtime] Received:', data.type, data);

      switch (data.type) {
        case 'notification':
          if (data.data?.type === 'new_message') {
            toast.info(`New message: ${data.data.preview}`);
          } else if (data.data?.type === 'ride_request') {
            toast.info('New ride request!');
            invalidateRequestAndNotificationCaches();
          } else if (data.data?.type === 'request_accepted') {
            toast.success('Your ride request was accepted!');
            invalidateRequestAndNotificationCaches();
          } else if (data.data?.type === 'request_rejected') {
            toast.info('Your ride request was declined.');
            invalidateRequestAndNotificationCaches();
          } else if (data.data?.rideId && data.message) {
            toast.info(data.message);
            invalidateRequestAndNotificationCaches();
          }
          break;

        case 'new_message':
          queryClient.invalidateQueries({ queryKey: ['messages', data.conversationId] });
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
          break;

        case 'ride_update':
        case 'ride_status_changed':
          if (data.rideId && (data.tripStatus || data.tripStatusRider || data.tripStatusDriver || data.ride)) {
            syncRideCaches((old: any[]) =>
              old.map((ride) => {
                if (ride.id !== data.rideId) return ride;

                const nextRide = data.ride ?? {};
                return {
                  ...ride,
                  ...nextRide,
                  driver: nextRide.driver
                    ? {
                        ...ride.driver,
                        ...nextRide.driver,
                      }
                    : ride.driver,
                  tripStatus: data.tripStatus ?? nextRide.tripStatus ?? ride.tripStatus,
                  tripStatusRider: data.tripStatusRider ?? nextRide.tripStatusRider ?? ride.tripStatusRider,
                  tripStatusDriver: data.tripStatusDriver ?? nextRide.tripStatusDriver ?? ride.tripStatusDriver,
                  status: data.tripStatus ?? nextRide.tripStatus ?? ride.status,
                };
              })
            );
          } else {
            syncRideCaches();
          }
          break;

        case 'ride_request_updated':
          invalidateRequestAndNotificationCaches();
          break;

        case 'ride_created':
          syncRideCaches();
          toast.success('New ride available!');
          break;
      }
    } catch (e) {
      console.error('[Realtime] Parse error:', e);
    }
  }, [invalidateRequestAndNotificationCaches, queryClient, syncRideCaches]);

  const scheduleReconnect = useCallback((delayMs: number) => {
    if (!shouldReconnect.current) return;
    clearTimeout(reconnectTimeout.current);
    reconnectTimeout.current = setTimeout(() => {
      isConnecting.current = false;
      connect();
    }, delayMs);
  }, []);

  const connectWebSocket = useCallback(() => {
    if (!userId || isConnecting.current) return;

    isConnecting.current = true;
    ws.current?.close();
    
    console.log('[WS] Connecting to:', `${currentWSUrl}?userId=${userId}`);
    ws.current = new WebSocket(`${currentWSUrl}?userId=${userId}`);
    
    ws.current.onerror = (error) => {
      console.warn('[WS] Connection error, retrying:', error);
      currentWSUrl = WS_URL;
      scheduleReconnect(1000);
    };
    
    ws.current.onopen = () => {
      isConnecting.current = false;
      clearTimeout(reconnectTimeout.current);
      console.log('[WS] Connected successfully to', currentWSUrl);
    };

    ws.current.onmessage = (event) => handleRealtimeMessage(event.data);

    ws.current.onclose = () => {
      isConnecting.current = false;
      scheduleReconnect(3000);
    };
  }, [handleRealtimeMessage, scheduleReconnect, userId]);

  const connectSocketIo = useCallback(() => {
    if (!userId || isConnecting.current) return;

    isConnecting.current = true;
    socketRef.current?.disconnect();

    console.log('[Socket.IO] Connecting to:', currentSocketIoUrl);
    const socket = io(currentSocketIoUrl, {
      auth: { userId },
      query: { userId },
      transports: ['websocket', 'polling'],
      reconnection: false,
      timeout: 10000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      isConnecting.current = false;
      clearTimeout(reconnectTimeout.current);
      console.log('[Socket.IO] Connected successfully to', currentSocketIoUrl);
    });

    socket.on('connect_error', (error) => {
      console.warn('[Socket.IO] Connection error, retrying with WebSocket:', error);
      socket.disconnect();
      currentSocketIoUrl = SOCKET_IO_URL;
      connectWebSocket();
    });

    socket.on('disconnect', () => {
      isConnecting.current = false;
      scheduleReconnect(3000);
    });

    [
      'notification',
      'new_message',
      'ride_update',
      'ride_status_changed',
      'ride_request_updated',
      'ride_created',
    ].forEach((eventName) => {
      socket.on(eventName, (payload: any) => {
        handleRealtimeMessage(
          payload && typeof payload === 'object' && !Array.isArray(payload)
            ? { type: eventName, ...payload }
            : { type: eventName, data: payload }
        );
      });
    });
  }, [connectWebSocket, handleRealtimeMessage, scheduleReconnect, userId]);

  const connect = useCallback(() => {
    connectSocketIo();
  }, [connectSocketIo]);

  // Polling fallback every 10s
  useEffect(() => {
    shouldReconnect.current = true;
    const interval = setInterval(() => {
      if (userId) {
        syncRideCaches();
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
    }, 10000);
    
    connect();
    return () => {
      shouldReconnect.current = false;
      isConnecting.current = false;
      clearInterval(interval);
      clearTimeout(reconnectTimeout.current);
      socketRef.current?.disconnect();
      ws.current?.close();
    };
  }, [connect, userId, queryClient, syncRideCaches]);

  return {
    sendWsMessage: (data: any) => {
      if (socketRef.current?.connected) {
        const eventName = typeof data?.type === 'string' ? data.type : 'message';
        socketRef.current.emit(eventName, data);
        return;
      }

      ws.current?.send(JSON.stringify(data));
    },
  };
}
